export enum FilterOperator {
  CONTAINS = "contains",
  EQUALS = "equals",
  NOT_EQUALS = "!=",
  STARTS_WITH = "startsWith",
  ENDS_WITH = "endsWith",
  IS_EMPTY = "isEmpty",
  IS_NOT_EMPTY = "isNotEmpty",
  IS_ANY_OF = "isAnyOf",
  IS = "is",
  IS_NOT = "not",
  IS_AFTER = "after",
  IS_ON_OR_AFTER = "onOrAfter",
  IS_BEFORE = "before",
  IS_ON_OR_BEFORE = "onOrBefore",
  GREATER_THAN = ">",
  GREATER_THAN_OR_EQUAL = ">=",
  LESS_THAN = "<",
  LESS_THAN_OR_EQUAL = "<=",
}

export enum FilterLogicalOperator {
  AND = "and",
  OR = "or",
}

export type FilterValue = string | null;
export type FilterFieldType = "string" | "date" | "number" | "enum";
export type FilterList = FilterValue[];

export type FilterOperatorConfig = {
  operator: FilterOperator;
  value: string | FilterList;
};

export type DataGridFilter = {
  field: string;
  operator: FilterOperator;
  value: string | FilterList;
};

export type BackendFilters = {
  filters?: Record<string, string[]>;
  datagridFilters?: DataGridFilter[];
  logicalOperator?: FilterLogicalOperator;
};

export interface BackendFilterableSearchRequest {
  page: number;
  page_size: number;
  sort: string[];
  sort_direction: "asc" | "desc";
  filters: Record<string, string[]>;
  datagrid_filters?: DataGridFilter[];
  logical_operator?: FilterLogicalOperator;
  export_mode?: boolean;
}

export const createDefaultBackendFilters = (): BackendFilters => ({
  filters: {},
  datagridFilters: [],
  logicalOperator: FilterLogicalOperator.AND,
});

export const getFilterOperator = (op: string): FilterOperator => {
  const operatorMap: Record<string, FilterOperator> = {
    isEmpty: FilterOperator.IS_EMPTY,
    isNotEmpty: FilterOperator.IS_NOT_EMPTY,
    isAnyOf: FilterOperator.IS_ANY_OF,
    equals: FilterOperator.EQUALS,
    contains: FilterOperator.CONTAINS,
    startsWith: FilterOperator.STARTS_WITH,
    endsWith: FilterOperator.ENDS_WITH,
    is: FilterOperator.IS,
    not: FilterOperator.IS_NOT,
    after: FilterOperator.IS_AFTER,
    onOrAfter: FilterOperator.IS_ON_OR_AFTER,
    before: FilterOperator.IS_BEFORE,
    onOrBefore: FilterOperator.IS_ON_OR_BEFORE,
    "!=": FilterOperator.NOT_EQUALS,
    ">": FilterOperator.GREATER_THAN,
    ">=": FilterOperator.GREATER_THAN_OR_EQUAL,
    "<": FilterOperator.LESS_THAN,
    "<=": FilterOperator.LESS_THAN_OR_EQUAL,
  };
  return operatorMap[op] ?? FilterOperator.EQUALS;
};

interface FilterModelItem {
  field: string;
  operator: string;
  value?: unknown;
}

interface FilterModel {
  items: FilterModelItem[];
  logicOperator?: string;
}

/**
 * Converts a DataGrid filter model into BackendFilters, optionally renaming
 * grid columns to the field names the search endpoint expects.
 */
export const convertFilterModelToBackendFilters = (
  model: FilterModel,
  fieldMapper: (field: string) => string = (field) => field,
): BackendFilters => {
  const backendFilters = createDefaultBackendFilters();

  if (!model.items || model.items.length === 0) return backendFilters;

  model.items.forEach((item) => {
    const backendField = fieldMapper(item.field);
    const { value, operator } = item;

    if (value === undefined && !["isEmpty", "isNotEmpty"].includes(operator)) {
      return;
    }

    const filterOperator = getFilterOperator(operator);

    if (operator === "isAnyOf" && Array.isArray(value)) {
      const filteredValues = value
        .filter(
          (v: unknown) =>
            v !== null && v !== undefined && String(v).trim() !== "",
        )
        .map((v: unknown) => String(v).trim());

      if (filteredValues.length > 0) {
        (backendFilters.datagridFilters ?? []).push({
          field: backendField,
          operator: filterOperator,
          value: filteredValues,
        });
      }
    } else if (["isEmpty", "isNotEmpty"].includes(operator)) {
      (backendFilters.datagridFilters ?? []).push({
        field: backendField,
        operator: filterOperator,
        value: "",
      });
    } else if (value !== undefined && value !== null) {
      (backendFilters.datagridFilters ?? []).push({
        field: backendField,
        operator: filterOperator,
        value: String(value),
      });
    }
  });

  backendFilters.logicalOperator =
    model.logicOperator as unknown as FilterLogicalOperator;

  return backendFilters;
};

/**
 * Reads a persisted DataGrid filter model from localStorage and converts it to BackendFilters.
 */
export const getPersistedBackendFilters = (
  persistKey: string,
  fieldMapper: (field: string) => string = (field) => field,
): BackendFilters => {
  try {
    const raw = localStorage.getItem(persistKey);
    if (!raw) return createDefaultBackendFilters();

    const parsed = JSON.parse(raw);
    const filterModel = parsed?.filter?.filterModel;

    if (!filterModel?.items?.length) return createDefaultBackendFilters();

    return convertFilterModelToBackendFilters(filterModel, fieldMapper);
  } catch {
    return createDefaultBackendFilters();
  }
};

export const validateBackendFilters = (filters: BackendFilters): string[] => {
  const errors: string[] = [];

  if (!filters) {
    errors.push("Filters object is required");
    return errors;
  }

  // Validate datagrid filters
  if (filters.datagridFilters) {
    filters.datagridFilters.forEach((filter, index) => {
      if (!filter.field) {
        errors.push(`Filter ${index}: field is required`);
      }
      if (!filter.operator) {
        errors.push(`Filter ${index}: operator is required`);
      }

      // Check if value is required for this operator
      const operatorsWithoutValues = ["isEmpty", "isNotEmpty"];
      if (
        !operatorsWithoutValues.includes(filter.operator) &&
        (filter.value === undefined || filter.value === null)
      ) {
        errors.push(
          `Filter ${index}: value is required for operator ${filter.operator}`,
        );
      }

      // Validate operator is a known FilterOperator
      if (
        !Object.values(FilterOperator).includes(
          filter.operator as FilterOperator,
        )
      ) {
        errors.push(`Filter ${index}: invalid operator ${filter.operator}`);
      }
    });
  }

  if (
    filters.logicalOperator &&
    !Object.values(FilterLogicalOperator).includes(filters.logicalOperator)
  ) {
    errors.push(`Invalid logical operator: ${filters.logicalOperator}`);
  }

  return errors;
};
